import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box, Snackbar, Alert } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import {
  editionDocumentationWorkflow,
  EDITION_DOCUMENTATION_WORKFLOW_ERROR_STATES,
} from "../../../../workflows/workflowDefinitions";
import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { EditionViewStore } from "../../store/EditionViewStore";
import { useRootStore } from "../../../../store/contexts";
import { WorkflowRoadmap } from "../../../../common/controls";
import { ShowMultiLine } from "../../../common/controls";
import { ShowDate } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { EditionContentType } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { ShowReferenceSet } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { InputHtmlString } from "../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { GridColumn } from "../../../common/types";
import { ShowDateTime } from "../../../common/controls";
import {
  SharesPieChart,
  computeSharesFromWorks,
} from "../../../../common/components/SharesPieChart";
import { PDFViewerDialog } from "../../../../common/components";
import { EditionSignatureStatusCard } from "../EditionSignatureStatusCard";


export type EditionViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  documentationStateValue: string;
  documentationLastOperationalErrorValue?: {
    displayId: string;
    id: string;
  } | null;
  documentationLastOperationalError: string;
  publicationDateValue?: { displayId: string; id: string } | null;
  publicationDate: string | null;
  ejemplarPdfFiles: CrudRows;
  ejemplarPdfViewAction?: IMenuAction[];
  nameValue?: { displayId: string; id: string } | null;
  name: string;
  editionTypeValue: { displayId: string; id: string } | null;
  contentTypeOptions: { id: string; displayId: string }[];
  contentType: EditionContentType;
  publicationLink?: string;
  editionNumberValue?: { displayId: string; id: string } | null;
  editionNumber: number;
  priceValue?: { displayId: string; id: string } | null;
  price: number;
  worksValues: { displayId: string; id: string }[];
  viewFormWorks?: (id: string) => void;
  notesValue?: { displayId: string; id: string } | null;
  notes: string;
  dndaExpedienteNumberValue?: { displayId: string; id: string } | null;
  dndaExpedienteNumber: string;
  contractObservationValue?: { displayId: string; id: string } | null;
  contractObservation: string;
  documentationRows: CrudRows;
  documentationVisibleColumns: GridColumn[];
  documentationSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  associatedDocumentsRows: CrudRows;
  associatedDocumentsVisibleColumns: GridColumn[];
  associatedDocumentsSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  userAttachmentsRows: CrudRows;
  userAttachmentsVisibleColumns: GridColumn[];
  userAttachmentsSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  createdAt_Value?: { displayId: string; id: string } | null;
  createdAt_: Date | null;
  lastUpdatedAt_Value?: { displayId: string; id: string } | null;
  lastUpdatedAt_: Date | null;
  editionViewStore: EditionViewStore;
  worksWithAuthors?: any[];
};

export const EditionViewForm: React.FC<EditionViewFormProps> = observer(
  ({
    onPrint = () => {},
    onCancel = () => {},
    onEdit,
    showToolbox = true,
    documentationStateValue = "",
    documentationLastOperationalError = "",
    publicationDate = null,
    ejemplarPdfFiles,
    ejemplarPdfViewAction,
    name = "",
    editionTypeValue,
    contentTypeOptions = [],
    contentType = "" as EditionContentType,
    publicationLink = "",
    editionNumber = 0,
    price = 0,
    worksValues,
    viewFormWorks = () => {},
    notes = "",
    dndaExpedienteNumber = "",
    contractObservation = "",
    documentationRows,
    documentationVisibleColumns,
    documentationSetColumnWiths,
    associatedDocumentsRows,
    associatedDocumentsVisibleColumns,
    associatedDocumentsSetColumnWiths,
    userAttachmentsRows,
    userAttachmentsVisibleColumns,
    userAttachmentsSetColumnWiths,
    createdAt_ = null,
    lastUpdatedAt_ = null,
    editionViewStore,
    worksWithAuthors = [],
  }) => {
    const rootStore = useRootStore();
    const { authStore, langStore } = rootStore;
    // Los errores del circuito de documentación son material del operador: el
    // cliente ve el hito en curso, no el detalle técnico (mismo criterio que
    // SignedEdition). El layout de este form es manual, así que el gate por rol
    // que el generador aplica sobre .visibleForRoles se replica acá a mano.
    const isSysAdmin = !!authStore.hasRole("SysAdmin");
    const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: "success" | "error" | "warning" | "info" }>({
      open: false, message: "", severity: "success"
    });
    const [recheckLoading, setRecheckLoading] = React.useState(false);

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    const handleResend = async (editionDocumentationId: string, email: string) => {
      try {
        await editionViewStore.resendSignatureEmail(editionDocumentationId, email);
        setSnackbar({ open: true, message: `Email reenviado a ${email}`, severity: "success" });
      } catch {
        setSnackbar({ open: true, message: "Error al reenviar el email", severity: "error" });
      }
    };

    const handleRecheck = async () => {
      const editionId = editionViewStore.editionInstance?.id;
      if (!editionId) return;
      setRecheckLoading(true);
      try {
        const result: any = await editionViewStore.recheckSignatures(editionId);
        if (result?.status === "transitioned") {
          setSnackbar({ open: true, message: "Firmas completas — la edición pasó a Firmadas. Actualizá la lista.", severity: "success" });
        } else if (result?.status === "pending") {
          const faltan = (result.pendingDocs ?? []).map((d: any) => d.contractNumber || "?").join(", ");
          const unlinked = (result.pendingDocs ?? []).some((d: any) => d.signatureFoundButUnlinked);
          setSnackbar({
            open: true,
            message: `Faltan firmas: ${faltan}.${unlinked ? " Hay firmas completadas sin vincular (revisar)." : ""}`,
            severity: "warning",
          });
        } else {
          setSnackbar({ open: true, message: result?.reason || "No se pudo completar la verificación.", severity: "info" });
        }
      } catch {
        setSnackbar({ open: true, message: "Error al re-verificar firmas", severity: "error" });
      } finally {
        setRecheckLoading(false);
      }
    };

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            editForm={onEdit}
            printForm={() => onPrint(handlePrint)}
            closeForm={() => onCancel()}
          />
        )}
        <Box ref={componentRef}>
          <GroupContainer
            id="editionData"
            title={langStore.groupEditionEditionDataLabel}
            collapsible={true}
            expanded={editionViewStore.getCardState("editionData")}
            toggleExpand={() => editionViewStore.toggleCardState("editionData")}
          >
            <FormControl fullWidth>
              <WorkflowRoadmap
                workflow={editionDocumentationWorkflow}
                currentState={documentationStateValue}
                labels={langStore.EditionDocumentationStateStateLabels}
                operationalError={documentationLastOperationalError}
                concealErrors={!isSysAdmin}
                transitions={[]}
                onTransition={() => {}}
                isProcessing={false}
                errorStates={EDITION_DOCUMENTATION_WORKFLOW_ERROR_STATES}
              />
            </FormControl>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "0 0 400px" }}>
                <FormControl fullWidth>
                  <ShowMultiLine value={name} label={langStore.EditionNameLabel} />
                  <ShowReference
                    label={langStore.EditionEditionTypeLabel}
                    selectedValue={langStore.displayIdByLang(
                      editionTypeValue?.displayId
                    )}
                  />
                  <ShowString
                    value={
                      contentType
                        ? contentTypeOptions.find(
                            (o: { id: string }) => o.id === contentType
                          )?.displayId || contentType
                        : ""
                    }
                    label={langStore.EditionContentTypeLabel}
                  />
                  {publicationLink && (
                    <ShowMultiLine
                      value={publicationLink}
                      label={langStore.EditionPublicationLinkLabel}
                    />
                  )}
                  <ShowNumber
                    value={editionNumber}
                    label={langStore.EditionEditionNumberLabel}
                  />
                  <ShowNumber
                    value={price}
                    label={langStore.EditionPriceLabel}
                    decimalScale={2}
                    prefix={"$"}
                    thousandSeparator={","}
                    fixedDecimalScale={true}
                  />
                  <ShowReferenceSet
                    label={langStore.EditionWorksLabel}
                    selectedValues={worksValues}
                    onView={viewFormWorks}
                  />
                  <ShowMultiLine
                    value={notes}
                    label={langStore.EditionNotesLabel}
                  />
                  <ShowMultiLine
                    value={dndaExpedienteNumber}
                    label={langStore.EditionDndaExpedienteNumberLabel}
                  />
                  <ShowDate
                    value={publicationDate}
                    label={langStore.EditionPublicationDateLabel}
                    locale={langStore.timeFormat}
                  />
                  <ShowFilesCompact
                    rows={ejemplarPdfFiles}
                    showLabel={true}
                    label={langStore.EditionEjemplarPdfLabel}
                    menuActions={ejemplarPdfViewAction}
                    columns={[
                      { name: "mimetype", title: langStore.fileTypeLabel },
                      { name: "name", title: langStore.fileNameLabel },
                      { name: "formattedSize", title: langStore.fileSizeLabel },
                    ]}
                  />
                  {isSysAdmin && (
                    <ShowMultiLine
                      value={documentationLastOperationalError}
                      label={
                        langStore.EditionDocumentationLastOperationalErrorLabel
                      }
                    />
                  )}
                </FormControl>
              </Box>
              {worksWithAuthors.length > 0 && (
                <Box sx={{ flex: "1 1 400px", minWidth: 400 }}>
                  <SharesPieChart
                    data={computeSharesFromWorks(worksWithAuthors)}
                    title={
                      worksWithAuthors.length > 1
                        ? "Distribución Promedio Autores y Editoriales"
                        : "Distribución Autores y Editoriales"
                    }
                    subtitle={
                      worksWithAuthors.length > 1
                        ? `Promedio de participación neta sobre ${worksWithAuthors.length} obras`
                        : "Participación neta de autores y editoriales"
                    }
                    valueLabel={
                      worksWithAuthors.length > 1
                        ? "Promedio"
                        : "Participación"
                    }
                  />
                </Box>
              )}
            </Box>
          </GroupContainer>

          <EditionSignatureStatusCard
            signatureStatuses={editionViewStore.signatureStatuses}
            documentationStateValue={documentationStateValue}
            expanded={editionViewStore.getCardState("signatureStatus")}
            onToggle={() => editionViewStore.toggleCardState("signatureStatus")}
            onResend={handleResend}
            canRecheck={editionViewStore.canRecheckSignatures}
            onRecheck={handleRecheck}
            recheckLoading={recheckLoading}
          />

          <GroupContainer
            id="contracts"
            title={langStore.groupEditionContractsLabel}
            collapsible={true}
            expanded={editionViewStore.getCardState("contracts")}
            toggleExpand={() => editionViewStore.toggleCardState("contracts")}
          >
            <FormControl fullWidth>
              <InputHtmlString
                value={contractObservation}
                onChange={() => {}}
                label={langStore.EditionContractObservationLabel}
                readOnly={true}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="editionContracts"
            title={langStore.groupEditionEditionContractsLabel}
            collapsible={true}
            expanded={editionViewStore.getCardState("editionContracts")}
            toggleExpand={() =>
              editionViewStore.toggleCardState("editionContracts")
            }
          >
            <FormControl fullWidth>
              <ShowItemsCompact
                rows={documentationRows}
                showLabel={false}
                label={langStore.EditionDocumentationLabel}
                columns={documentationVisibleColumns}
                setColumnWidths={documentationSetColumnWiths}
                entityName="EditionDocumentation"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal("editionDocumentation", rowId)
                }
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="editionDocumentation"
            title={langStore.groupEditionEditionDocumentationLabel}
            collapsible={true}
            expanded={editionViewStore.getCardState("editionDocumentation")}
            toggleExpand={() =>
              editionViewStore.toggleCardState("editionDocumentation")
            }
          >
            <FormControl fullWidth>
              <ShowItemsCompact
                rows={associatedDocumentsRows}
                showLabel={false}
                label={langStore.EditionAssociatedDocumentsLabel}
                columns={associatedDocumentsVisibleColumns}
                setColumnWidths={associatedDocumentsSetColumnWiths}
                entityName="AssociatedDocumentEdition"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal(
                    "associatedDocumentEdition",
                    rowId
                  )
                }
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="editionUserAttachments"
            title={langStore.groupEditionEditionUserAttachmentsLabel}
            collapsible={true}
            expanded={editionViewStore.getCardState("editionUserAttachments")}
            toggleExpand={() =>
              editionViewStore.toggleCardState("editionUserAttachments")
            }
          >
            <FormControl fullWidth>
              <ShowItemsCompact
                rows={userAttachmentsRows}
                showLabel={false}
                label={langStore.EditionUserAttachmentsLabel}
                columns={userAttachmentsVisibleColumns}
                setColumnWidths={userAttachmentsSetColumnWiths}
                entityName="UserAttachmentEdition"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal("userAttachmentEdition", rowId)
                }
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="systemDates"
            title={langStore.groupEditionSystemDatesLabel}
            collapsible={true}
            expanded={editionViewStore.getCardState("systemDates")}
            toggleExpand={() => editionViewStore.toggleCardState("systemDates")}
          >
            <FormControl fullWidth>
              <ShowDateTime
                value={createdAt_}
                label={langStore.EditionCreatedAt_Label}
              />
              <ShowDateTime
                value={lastUpdatedAt_}
                label={langStore.EditionLastUpdatedAt_Label}
              />
            </FormControl>
          </GroupContainer>
        </Box>
        <PDFViewerDialog
          open={editionViewStore.pdfViewerOpen}
          onClose={() => editionViewStore.closePdfViewer()}
          pdfUrl={editionViewStore.pdfViewerUrl}
          title={editionViewStore.pdfViewerTitle}
          warning={editionViewStore.pdfViewerWarning}
        />
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
        </Snackbar>
      </Grid>
    );
  }
);
